import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { Blog, BlogHome, ContentPageTheme } from "@/services/bedrock";
import { BlogHomeDraft, toBlogHomePreviewPayload } from "./blogHomePreviewPayload";
import { LivePreviewFrame, useLivePreviewIframe } from "./LivePreviewFrame";

export type BlogHomePreviewPart = "title" | "subtitle" | "photo";

export type BlogHomePreviewHandle = {
  scrollToPart: (part: BlogHomePreviewPart) => void;
  highlightPart: (part: BlogHomePreviewPart | null) => void;
};

interface BlogHomePreviewProps {
  home: BlogHome;
  draft: BlogHomeDraft;
  blogs?: Blog[];
  theme?: ContentPageTheme | null;
  shareUrl?: string;
  onCopyShareUrl?: () => void;
  onOpenPage?: () => void;
  className?: string;
}

export const BlogHomePreview = forwardRef<BlogHomePreviewHandle, BlogHomePreviewProps>(
  ({ home, draft, blogs = [], theme, shareUrl, onCopyShareUrl, onOpenPage, className }, ref) => {
    const { iframeRef, iframeOrigin, postToPreview, onIframeLoad } = useLivePreviewIframe();
    const payload = useMemo(() => toBlogHomePreviewPayload(home, draft, theme, blogs), [home, draft, theme, blogs]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToPart: (part: BlogHomePreviewPart) => {
          postToPreview({ type: "BLOG_HOME_PART", action: "scroll", part });
        },
        highlightPart: (part: BlogHomePreviewPart | null) => {
          postToPreview({ type: "BLOG_HOME_PART", action: part == null ? "clear" : "highlight", part });
        },
      }),
      [postToPreview]
    );

    useEffect(() => {
      postToPreview({ type: "BLOG_HOME", payload });
    }, [postToPreview, payload]);

    return (
      <LivePreviewFrame
        iframeRef={iframeRef}
        iframeOrigin={iframeOrigin}
        srcPath="/b/p/preview"
        title="Blog home preview"
        openPageAriaLabel="Open blog home"
        shareUrl={shareUrl}
        onCopyShareUrl={onCopyShareUrl}
        onOpenPage={onOpenPage}
        onIframeLoad={onIframeLoad}
        className={className}
      />
    );
  }
);

BlogHomePreview.displayName = "BlogHomePreview";
